import { useState, useEffect, useCallback } from "react";
import { collection, getDocs, doc, setDoc, query, where, deleteField, serverTimestamp } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../services/firebase";
import Preacher, { preacherConverter } from "../DTOs/Preacher";
import { Globals } from "../globals";
import { gemeindeSubPath } from "../services/gemeindePaths";
import { useGemeinde } from "../context/GemeindeContext";
import { useTranslation } from "react-i18next";
import Form from "react-bootstrap/esm/Form";
import Button from "react-bootstrap/esm/Button";
import Table from "react-bootstrap/esm/Table";
import Spinner from "react-bootstrap/esm/Spinner";
import I_IMC_Props from "../infos/I_IMC_Props";
import { handleFirebaseError } from "../utils/errorHandler";
import { useAuth } from "../hooks/useAuth";
import { loadAbsencesByPreacher } from "../services/absenceService";
import { AbsenceMap, countWorkload, formatAbsence, Workload } from "../utils/absences";
import PreacherDeleteDialog from "./PreacherDeleteDialog";
import Badge from "react-bootstrap/esm/Badge";

type SortKey = "name" | "quarter" | "year";
type ConsentSource = "admin-oral" | "admin-written";

const EMPTY_FORM = {
    name: "",
    surname: "",
    churchFunction: "",
    email: "",
    phoneNumber: "",
    consentEmail: false,
    consentWhatsApp: false,
    consentSource: "admin-oral" as ConsentSource,
};

/**
 * The consent fields to write: only channels whose state changed, so the time
 * and source of an earlier record (e.g. from "Mein Plan") stay as they were.
 */
function consentUpdates(
    before: Preacher["consents"] | undefined,
    form: { consentEmail: boolean; consentWhatsApp: boolean; consentSource: ConsentSource }
): Record<string, unknown> {
    const updates: Record<string, unknown> = {};
    const wanted = { email: form.consentEmail, whatsapp: form.consentWhatsApp };
    for (const channel of ["email", "whatsapp"] as const) {
        if ((before?.[channel]?.given === true) !== wanted[channel]) {
            updates[channel] = { given: wanted[channel], at: serverTimestamp(), source: form.consentSource };
        }
    }
    return updates;
}

export default function PreacherManagement({ setVariant, setMessage, setShowMessage }: I_IMC_Props) {
    const { t } = useTranslation();
    const { isAuthenticated, loading: authLoading } = useAuth();
    const { activeGemeindeId } = useGemeinde();
    const serviceOptions = ["Verkündiger", "Prediger", "Diakon", "Ältester"] as const;
    const [preachers, setPreachers] = useState<Preacher[]>([]);
    const [loading, setLoading] = useState(false);
    const [editing, setEditing] = useState<string | null>(null);
    // Absences the preachers entered themselves, and how often each one serves this quarter / year.
    const [absences, setAbsences] = useState<AbsenceMap>(new Map());
    const [workload, setWorkload] = useState<Map<string, Workload>>(new Map());
    const [sortKey, setSortKey] = useState<SortKey>("name");
    // Key of the preacher whose access link is being sent or revoked.
    const [accessBusy, setAccessBusy] = useState<string | null>(null);
    // Archived preachers are hidden from the list unless asked for (GOD-33).
    const [showArchived, setShowArchived] = useState(false);
    const [preacherToDelete, setPreacherToDelete] = useState<Preacher | null>(null);

    // Form state
    const [formData, setFormData] = useState({
        name: '',
        surname: '',
        churchFunction: '',
        email: '',
        phoneNumber: '',
        // GOD-35: consent per channel and how the admin obtained it.
        consentEmail: false,
        consentWhatsApp: false,
        consentSource: 'admin-oral' as ConsentSource,
    });

    const loadPreachers = useCallback(
        async function loadPreachers() {
            if (!activeGemeindeId) {
                setLoading(false);
                return;
            }
            setLoading(true);
            try {
                const querySnapshot = await getDocs(collection(db, ...gemeindeSubPath(activeGemeindeId, Globals.PREACHER_SUBCOLLECTION)));
                const preacherList: Preacher[] = [];
                querySnapshot.forEach((doc) => {
                    const preacher = preacherConverter.fromFirestore(doc, {});
                    preacherList.push(preacher);
                });
                preacherList.sort((a, b) => (a.name + a.surname).localeCompare(b.name + b.surname));
                setPreachers(preacherList);
                setAbsences(await loadAbsencesByPreacher(activeGemeindeId, preacherList.map((p) => p.key)));

                // Workload: the services of the current year (only the fields that are counted).
                const now = new Date();
                const yearSnapshot = await getDocs(
                    query(
                        collection(db, ...gemeindeSubPath(activeGemeindeId, Globals.SUNDAY_SERVICE_SUBCOLLECTION)),
                        where("date", ">=", new Date(now.getFullYear(), 0, 1)),
                        where("date", "<", new Date(now.getFullYear() + 1, 0, 1))
                    )
                );
                setWorkload(
                    countWorkload(
                        yearSnapshot.docs.map((d) => ({
                            date: d.data().date.toDate() as Date,
                            sermon1PreacherKey: d.data().sermon1PreacherKey as string | undefined,
                            sermon2PreacherKey: d.data().sermon2PreacherKey as string | undefined,
                        })),
                        now
                    )
                );
            } catch (error) {
                setVariant("danger");
                setMessage(handleFirebaseError(error, "loadPreachers"));
                setShowMessage(true);
            }
            setLoading(false);
        },
        [setVariant, setMessage, setShowMessage, activeGemeindeId],
    );

    useEffect(() => {
        // Don't load data if not authenticated or still checking auth
        if (authLoading || !isAuthenticated) {
            return;
        }
        loadPreachers();
    }, [isAuthenticated, authLoading, loadPreachers]);

    async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault();
        if (!activeGemeindeId) return;
        setLoading(true);

        try {
            if (editing) {
                // Update existing preacher without overwriting creation timestamp
                const docRef = doc(db, ...gemeindeSubPath(activeGemeindeId, Globals.PREACHER_SUBCOLLECTION, editing));
                const updateData: any = {
                    key: editing,
                    name: formData.name,
                    surname: formData.surname,
                    churchFunction: formData.churchFunction,
                    // An emptied field is removed, not kept with its old value (merge).
                    email: formData.email.trim() || deleteField(),
                    phoneNumber: formData.phoneNumber.trim() || deleteField(),
                };
                const consents = consentUpdates(preachers.find((p) => p.key === editing)?.consents, formData);
                if (Object.keys(consents).length > 0) updateData.consents = consents;

                await setDoc(docRef, updateData, { merge: true });
            } else {
                // Create new preacher with auto-generated ID
                const preacherCollectionRef = collection(db, ...gemeindeSubPath(activeGemeindeId, Globals.PREACHER_SUBCOLLECTION));
                const docRef = doc(preacherCollectionRef); // Generate a random ID
                const preacher = new Preacher(docRef.id, formData.name, formData.surname, formData.churchFunction, formData.email || undefined, formData.phoneNumber || undefined);
                const consents = consentUpdates(undefined, formData);
                await setDoc(docRef, {
                    ...preacherConverter.toFirestore(preacher),
                    ...(Object.keys(consents).length > 0 ? { consents } : {}),
                });
            }

            setVariant("success");
            setMessage(t("admin.preacherSaved"));
            setShowMessage(true);

            // Reset form
            setFormData(EMPTY_FORM);
            setEditing(null);
            await loadPreachers();
        } catch (error) {
            setVariant("danger");
            setMessage(handleFirebaseError(error, "savePreacher"));
            setShowMessage(true);
        }

        setLoading(false);
    }

    // Archiving is the normal way to retire a preacher: the name stays on past
    // services, the preacher can no longer be chosen, and the personal link is revoked.
    async function handleArchive(preacher: Preacher, archived: boolean) {
        if (!activeGemeindeId) return;
        const name = `${preacher.name} ${preacher.surname}`.trim();
        if (archived && !window.confirm(t("admin.archiveConfirm", { name }) as string)) return;
        setLoading(true);
        try {
            const ref = doc(db, ...gemeindeSubPath(activeGemeindeId, Globals.PREACHER_SUBCOLLECTION, preacher.key));
            await setDoc(
                ref,
                archived ? { archived: true, archivedAt: serverTimestamp() } : { archived: deleteField(), archivedAt: deleteField() },
                { merge: true }
            );
            if (archived) {
                const revoke = httpsCallable(functions, "revokePreacherAccess");
                await revoke({ gemeindeId: activeGemeindeId, preacherKey: preacher.key }).catch(() => undefined);
            }
            notify("success", t(archived ? "admin.preacherArchived" : "admin.preacherRestored", { name }));
            await loadPreachers();
        } catch (error) {
            notify("danger", handleFirebaseError(error, "archivePreacher"));
        }
        setLoading(false);
    }

    function notify(variant: "success" | "warning" | "danger", message: string) {
        setVariant(variant);
        setMessage(message);
        setShowMessage(true);
    }

    // Mails the preacher their personal link to "Mein Plan".
    async function handleSendAccessLink(preacher: Preacher) {
        if (!activeGemeindeId) return;
        const name = `${preacher.name} ${preacher.surname}`.trim();
        setAccessBusy(preacher.key);
        try {
            const send = httpsCallable<
                { gemeindeId: string; preacherKey: string },
                { sent: boolean; toRepresentative?: boolean; reason?: "email-disabled" | "no-email" }
            >(functions, "sendPreacherAccessLink");
            const { data } = await send({ gemeindeId: activeGemeindeId, preacherKey: preacher.key });
            if (data.sent) {
                notify("success", t(data.toRepresentative ? "myPlan.adminSentToRepresentative" : "myPlan.adminSent", { name }));
            } else {
                notify("warning", t(data.reason === "no-email" ? "myPlan.adminNoEmail" : "myPlan.adminEmailDisabled", { name }));
            }
        } catch (error: any) {
            notify("danger", error?.message || (t("errors.anErrorOccurred") as string));
        }
        setAccessBusy(null);
    }

    async function handleRevokeAccess(preacher: Preacher) {
        if (!activeGemeindeId) return;
        const name = `${preacher.name} ${preacher.surname}`.trim();
        if (!window.confirm(t("myPlan.adminRevokeConfirm", { name }) as string)) return;
        setAccessBusy(preacher.key);
        try {
            const revoke = httpsCallable<{ gemeindeId: string; preacherKey: string }, { revoked: number }>(
                functions,
                "revokePreacherAccess"
            );
            const { data } = await revoke({ gemeindeId: activeGemeindeId, preacherKey: preacher.key });
            notify(data.revoked > 0 ? "success" : "warning", t(data.revoked > 0 ? "myPlan.adminRevoked" : "myPlan.adminNothingToRevoke", { name }));
        } catch (error: any) {
            notify("danger", error?.message || (t("errors.anErrorOccurred") as string));
        }
        setAccessBusy(null);
    }

    function handleEdit(preacher: Preacher) {
        setEditing(preacher.key);
        setFormData({
            name: preacher.name,
            surname: preacher.surname,
            churchFunction: preacher.churchFunction,
            email: preacher.email || '',
            phoneNumber: preacher.phoneNumber || '',
            consentEmail: preacher.consents?.email?.given === true,
            consentWhatsApp: preacher.consents?.whatsapp?.given === true,
            consentSource: 'admin-oral',
        });
    }

    function handleCancel() {
        setEditing(null);
        setFormData(EMPTY_FORM);
    }

    const count = (key: string, field: "quarter" | "year") => workload.get(key)?.[field] ?? 0;
    const archivedCount = preachers.filter((p) => p.archived).length;
    const visiblePreachers = preachers.filter((p) => showArchived || !p.archived);
    const sortedPreachers =
        sortKey === "name"
            ? visiblePreachers
            : [...visiblePreachers].sort((a, b) => count(b.key, sortKey) - count(a.key, sortKey));
    const sortHeader = (key: SortKey, label: string) => (
        <th aria-sort={sortKey === key ? (key === "name" ? "ascending" : "descending") : "none"}>
            <button type="button" className="btn btn-link p-0 text-reset text-decoration-none fw-bold" onClick={() => setSortKey(key)}>
                {label} {sortKey === key ? "▾" : ""}
            </button>
        </th>
    );

    return (
        <div className="mx-auto" style={{ maxWidth: "1400px" }}>
            <h2>{t("admin.preacherManagement")}</h2>
            <br />

            <h4>{editing ? t("admin.editPreacher") : t("admin.addPreacher")}</h4>
            <Form onSubmit={(e) => handleSubmit(e)} id="PreacherForm">
                <Form.Group className="mb-3" controlId="PreacherForm.PreacherName">
                    <Form.Label>{t("admin.preacherName")}</Form.Label>
                    <Form.Control
                        type="text"
                        name="PreacherName"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                    />
                </Form.Group>
                <Form.Group className="mb-3" controlId="PreacherForm.PreacherSurname">
                    <Form.Label>{t("admin.preacherSurname")}</Form.Label>
                    <Form.Control
                        type="text"
                        name="PreacherSurname"
                        value={formData.surname}
                        onChange={(e) => setFormData({ ...formData, surname: e.target.value })}
                        required
                    />
                </Form.Group>
                <Form.Group className="mb-3" controlId="PreacherForm.ChurchFunction">
                    <Form.Label>{t("admin.churchFunction")}</Form.Label>
                    <Form.Control
                        as="select"
                        name="ChurchFunction"
                        value={formData.churchFunction}
                        onChange={(e) => setFormData({ ...formData, churchFunction: e.target.value })}
                    >
                        <option value="">{t("admin.churchFunctionPlaceholder") as string}</option>
                        {serviceOptions.map((option) => (
                            <option key={option} value={option}>
                                {option}
                            </option>
                        ))}
                    </Form.Control>
                </Form.Group>
                <Form.Group className="mb-3" controlId="PreacherForm.Email">
                    <Form.Label>{t("admin.preacherEmail")}</Form.Label>
                    <Form.Control
                        type="email"
                        name="Email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder={t("admin.preacherEmailPlaceholder") as string}
                        required={!editing}
                    />
                    {editing && <Form.Text className="text-muted">{t("admin.emailOptionalForExisting")}</Form.Text>}
                </Form.Group>
                <Form.Group className="mb-3" controlId="PreacherForm.PhoneNumber">
                    <Form.Label>{t('admin.preacherPhoneNumber')}</Form.Label>
                    <Form.Control 
                        type="tel" 
                        name="phoneNumber" 
                        value={formData.phoneNumber}
                        onChange={(e) => setFormData({...formData, phoneNumber: e.target.value})}
                        placeholder={t('admin.preacherPhoneNumberPlaceholder') as string}
                    />
                </Form.Group>
                <Form.Group className="mb-3" controlId="PreacherForm.Consent">
                    <Form.Label>{t("consent.formLabel")}</Form.Label>
                    <div className="d-flex flex-wrap gap-3 align-items-center">
                        <Form.Check
                            id="PreacherForm.ConsentEmail"
                            label={t("consent.email")}
                            checked={formData.consentEmail}
                            onChange={(e) => setFormData({ ...formData, consentEmail: e.target.checked })}
                        />
                        <Form.Check
                            id="PreacherForm.ConsentWhatsApp"
                            label={t("consent.whatsapp")}
                            checked={formData.consentWhatsApp}
                            onChange={(e) => setFormData({ ...formData, consentWhatsApp: e.target.checked })}
                        />
                        <Form.Select
                            size="sm"
                            style={{ maxWidth: "220px" }}
                            value={formData.consentSource}
                            onChange={(e) => setFormData({ ...formData, consentSource: e.target.value as ConsentSource })}
                            aria-label={t("consent.source") as string}
                        >
                            <option value="admin-oral">{t("consent.sourceOral")}</option>
                            <option value="admin-written">{t("consent.sourceWritten")}</option>
                        </Form.Select>
                    </div>
                    <Form.Text className="text-muted">{t("consent.formHelp")}</Form.Text>
                </Form.Group>
                <Form.Group controlId="PreacherForm.Submit">
                    <Button className="shadow-sm me-2" type="submit" disabled={loading}>
                        {t("admin.save")} {loading ? <Spinner size="sm" animation="grow" as="span" /> : ""}
                    </Button>
                    {editing && (
                        <Button variant="secondary" onClick={handleCancel}>
                            {t("common.cancel")}
                        </Button>
                    )}
                </Form.Group>
            </Form>

            <br />
            <hr />
            <br />

            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
                <h4 className="mb-0">{t("admin.preacherList")}</h4>
                {archivedCount > 0 && (
                    <Form.Check
                        type="switch"
                        id="preacher-show-archived"
                        checked={showArchived}
                        onChange={(e) => setShowArchived(e.target.checked)}
                        label={t("admin.showArchived", { count: archivedCount })}
                    />
                )}
            </div>
            {loading && <Spinner animation="border" />}
            <Table striped bordered hover responsive>
                <thead>
                    <tr>
                        {sortHeader("name", t('admin.preacherName'))}
                        <th>{t('admin.preacherSurname')}</th>
                        <th>{t('admin.preacherEmail')}</th>
                        <th>{t('admin.preacherPhoneNumber')}</th>
                        <th>{t('admin.churchFunction')}</th>
                        <th>{t("consent.column")}</th>
                        {sortHeader("quarter", t("absences.workloadQuarter"))}
                        {sortHeader("year", t("absences.workloadYear"))}
                        <th>{t("absences.column")}</th>
                        <th>{t('admin.actions')}</th>
                    </tr>
                </thead>
                <tbody>
                    {sortedPreachers.map((preacher) => (
                        <tr key={preacher.key} className={preacher.archived ? "text-muted" : undefined}>
                            <td>
                                {preacher.name}
                                {preacher.archived && (
                                    <Badge bg="secondary" className="ms-2">
                                        {t("admin.archivedBadge")}
                                    </Badge>
                                )}
                            </td>
                            <td>{preacher.surname}</td>
                            <td>{preacher.email || '-'}</td>
                            <td>{preacher.phoneNumber || '-'}</td>
                            <td>{preacher.churchFunction}</td>
                            <td className="small text-nowrap">
                                {(["email", "whatsapp"] as const).map((channel) => {
                                    const record = preacher.consents?.[channel];
                                    return (
                                        <div key={channel} title={record?.at ? record.at.toDate().toLocaleDateString(t("calendar.locale") as string) : undefined}>
                                            {record?.given ? "✓" : "–"} {t(channel === "email" ? "consent.email" : "consent.whatsapp")}
                                            {record?.given && record.source === "self-service" && (
                                                <span className="text-muted"> ({t("consent.sourceSelfShort")})</span>
                                            )}
                                        </div>
                                    );
                                })}
                            </td>
                            <td>{count(preacher.key, "quarter")}</td>
                            <td>{count(preacher.key, "year")}</td>
                            <td className="small" style={{ minWidth: "190px" }}>
                                {(absences.get(preacher.key) ?? []).map((absence) => (
                                    <div key={absence.id}>
                                        {formatAbsence(absence, t("calendar.locale") as string)}
                                        {absence.note && <span className="text-muted"> · {absence.note}</span>}
                                    </div>
                                ))}
                                {!absences.has(preacher.key) && t("absences.none")}
                            </td>
                            <td>
                                <div className="d-flex flex-wrap gap-1" style={{ minWidth: "320px" }}>
                                    <Button size="sm" variant="primary" onClick={() => handleEdit(preacher)}>
                                        {t("admin.edit")}
                                    </Button>
                                    {preacher.archived ? (
                                        <>
                                            <Button size="sm" variant="outline-primary" onClick={() => handleArchive(preacher, false)}>
                                                {t("admin.restore")}
                                            </Button>
                                            <Button size="sm" variant="danger" onClick={() => setPreacherToDelete(preacher)}>
                                                {t("admin.deletePermanently")}
                                            </Button>
                                        </>
                                    ) : (
                                        <>
                                            <Button size="sm" variant="outline-danger" onClick={() => handleArchive(preacher, true)}>
                                                {t("admin.archive")}
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="outline-primary"
                                                disabled={accessBusy === preacher.key}
                                                onClick={() => handleSendAccessLink(preacher)}
                                            >
                                                {t("myPlan.adminSend")}
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="outline-secondary"
                                                disabled={accessBusy === preacher.key}
                                                onClick={() => handleRevokeAccess(preacher)}
                                            >
                                                {t("myPlan.adminRevoke")}
                                            </Button>
                                        </>
                                    )}
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>

            <PreacherDeleteDialog
                gemeindeId={activeGemeindeId}
                preacher={preacherToDelete}
                onClose={() => setPreacherToDelete(null)}
                onDeleted={async () => {
                    const name = `${preacherToDelete?.name ?? ""} ${preacherToDelete?.surname ?? ""}`.trim();
                    setPreacherToDelete(null);
                    notify("success", t("admin.preacherDeletedPermanently", { name }));
                    await loadPreachers();
                }}
            />
        </div>
    );
}
